// Keeps long-lived tabs on the current release.
//
// After a deploy, a tab opened earlier still runs the old bundle. Its lazy
// route chunks can disappear from S3 and the page would hang on a spinner
// until the user pressed Ctrl+R. Guards:
//   1. watchForNewVersion: compares this build's sha (REACT_APP_BUILD_SHA) with
//      /version.json (no-store) on focus / visible / online / every 5 min. A new
//      build shows a "Reload" notice; if the tab was hidden when it was found,
//      or the notice was closed, the next in-app navigation (PUSH/POP, never
//      REPLACE) becomes a full page load. Never while a form has unsaved edits.
//      Builds without a sha, or a 404 for version.json (deploys before it
//      existed), fall back to comparing the main chunk in asset-manifest.json.
//      A redirect or 401/403 (Cloudflare Access / expired login) shows a
//      "session expired" notice and never reloads by itself.
//   2. installChunkErrorReload: if a chunk still fails to load, reload once,
//      and when that guard blocks a second reload show the Reload notice.
//
// Unsaved edits: call markDirty(true/false) from a form, or put
// data-dirty="true" on an element inside a <form>. No form reports this yet;
// there is no global form layer to hook, so each form opts in.

import React, {useEffect, useState} from 'react';
import {Button, ConfigProvider, notification} from 'antd';
import {getDesignTheme} from './designTheme';
import {isShoperApp} from './appType';
import {getThemeMode} from './uiPreferences';

const VERSION_URL = '/version.json';
const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 30 * 1000;
const RELOAD_GUARD_KEY = '2way.chunkReloadAt';
const RELOAD_GUARD_MS = 60 * 1000;
const NEW_VERSION_KEY = '2way-new-version';
const SESSION_EXPIRED_KEY = '2way-session-expired';

const DEFAULT_MESSAGES = {
    'appVersion.newTitle': 'A new version of 2way is available',
    'appVersion.newDescription': 'Reload to get the latest version. Your work is safe if you reload after saving.',
    'appVersion.reload': 'Reload',
    'appVersion.expiredTitle': 'Your session has expired',
    'appVersion.expiredDescription': 'Reload to sign in again.',
};

// The notices are created outside React, so they cannot use injectIntl. The
// App container hands over the current locale's messages (setNoticeMessages).
let noticeMessages = {};
export const setNoticeMessages = messages => {
    noticeMessages = messages || {};
};
const text = id => noticeMessages[id] || DEFAULT_MESSAGES[id];

// antd's static notification renders outside the app's ConfigProvider, so it
// would stay light in dark mode. holderRender wraps it in a ConfigProvider
// that follows the app's theme and language (pushed in by the App container).
// Starts from the same source as the App container, so it is right before the App renders.
let noticeTheme = {mode: isShoperApp() ? 'light' : getThemeMode(), shoper: isShoperApp(), antdLocale: undefined};
const themeListeners = new Set();
export const setNoticeTheme = (mode, shoper, antdLocale) => {
    if (noticeTheme.mode === mode && noticeTheme.shoper === shoper && noticeTheme.antdLocale === antdLocale) return;
    noticeTheme = {mode, shoper, antdLocale};
    // Called from App's render: notify after it, not during it.
    Promise.resolve().then(() => themeListeners.forEach(listener => listener()));
};

const ThemedHolder = ({children}) => {
    const [current, setCurrent] = useState(noticeTheme);
    useEffect(() => {
        const listener = () => setCurrent(noticeTheme);
        themeListeners.add(listener);
        listener();
        return () => themeListeners.delete(listener);
    }, []);
    return (
        <ConfigProvider locale={current.antdLocale} theme={getDesignTheme(current.mode, current.shoper)}>
            {children}
        </ConfigProvider>
    );
};
ConfigProvider.config({holderRender: children => <ThemedHolder>{children}</ThemedHolder>});

// ---- unsaved-edit guard ----
let dirty = false;
export const markDirty = value => {
    dirty = !!value;
};
export const isDirty = () => dirty || !!document.querySelector('form [data-dirty="true"]');

// ---- notices ----
const reloadNow = () => window.location.reload();

const reloadButton = () => <Button type="primary" size="small" onClick={reloadNow}>{text('appVersion.reload')}</Button>;

let autoReloadOnNavigation = false;

const showNewVersionNotice = () => {
    notification.info({
        key: NEW_VERSION_KEY,
        className: 'app-version-notice',
        duration: 0,
        placement: 'bottomRight',
        message: text('appVersion.newTitle'),
        description: text('appVersion.newDescription'),
        btn: reloadButton(),
        // Dismissed without reloading: the next navigation picks up the new build.
        onClose: () => {
            autoReloadOnNavigation = true;
        },
    });
};

const showSessionExpiredNotice = () => {
    notification.warning({
        key: SESSION_EXPIRED_KEY,
        className: 'app-version-notice',
        duration: 0,
        placement: 'bottomRight',
        message: text('appVersion.expiredTitle'),
        description: text('appVersion.expiredDescription'),
        btn: reloadButton(),
    });
};

// ---- version lookups ----
// The main bundle this tab is running, taken from its own <script> tag.
const currentMain = () => {
    const script = [...document.scripts].find(s => /\/static\/js\/main\.[0-9a-f]+\.chunk\.js$/.test(s.src));
    return script ? new URL(script.src).pathname : null;
};

const FETCH_OPTIONS = {cache: 'no-store', redirect: 'manual', credentials: 'same-origin'};

// A redirect (Cloudflare Access sends the browser to its login) or 401/403.
const isSessionResponse = response =>
    response.type === 'opaqueredirect' || response.status === 401 || response.status === 403;

const isJson = response => /json/i.test(response.headers.get('content-type') || '');

// Resolves to 'same' | 'new' | 'expired' | 'unknown'.
const checkAgainstManifest = async () => {
    const running = currentMain();
    if (!running) return 'unknown';
    const response = await fetch(MANIFEST_URL, FETCH_OPTIONS);
    if (isSessionResponse(response)) return 'expired';
    if (!response.ok || !isJson(response)) return 'unknown';
    const manifest = await response.json();
    const latest = manifest.files && manifest.files['main.js'];
    if (!latest) return 'unknown';
    return latest === running ? 'same' : 'new';
};

const BUILD_SHA = process.env.REACT_APP_BUILD_SHA;

const checkVersion = async () => {
    if (!BUILD_SHA) return checkAgainstManifest();
    const response = await fetch(VERSION_URL, FETCH_OPTIONS);
    if (isSessionResponse(response)) return 'expired';
    if (response.status === 404) {
        // Deployed before version.json existed (or a rollback): compare the manifest
        // for this check only, and try version.json again at the next trigger.
        return checkAgainstManifest();
    }
    if (!response.ok || !isJson(response)) return 'unknown';
    const latest = (await response.json()).sha;
    if (!latest) return 'unknown';
    return latest === BUILD_SHA ? 'same' : 'new';
};

export const isChunkLoadError = error =>
    !!error && (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message || ''));

// Reload at most once a minute, so a real outage cannot cause a reload loop.
// If sessionStorage is unavailable the guard cannot be kept, so don't reload.
export const reloadOnce = () => {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
        if (Date.now() - last < RELOAD_GUARD_MS) return false;
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch (e) {
        return false;
    }
    window.location.reload();
    return true;
};

export const watchForNewVersion = history => {
    // Development server: nothing to compare.
    if (process.env.NODE_ENV !== 'production') return;
    // No sha and no hashed main bundle: nothing to compare either.
    if (!BUILD_SHA && !currentMain()) return;

    let newVersion = false;
    let expired = false;
    let lastCheck = 0;
    let checking = false;

    const check = async () => {
        const now = Date.now();
        if (checking || now - lastCheck < MIN_CHECK_GAP_MS) return;
        checking = true;
        lastCheck = now;
        try {
            const state = await checkVersion();
            if (state === 'new' && !newVersion) {
                newVersion = true;
                // Found while the user was away: they will not see the notice,
                // so the next navigation reloads without asking.
                if (document.visibilityState === 'hidden') autoReloadOnNavigation = true;
                // A 403 may have shown the expired notice first; one notice at a time.
                notification.destroy(SESSION_EXPIRED_KEY);
                showNewVersionNotice();
            } else if (state === 'expired' && !expired) {
                expired = true;
                showSessionExpiredNotice();
            } else if (state !== 'expired' && state !== 'unknown' && expired) {
                // Signed in again (e.g. in another tab): drop the stale notice.
                expired = false;
                notification.destroy(SESSION_EXPIRED_KEY);
            }
        } catch (e) {
            // offline or blocked: try again at the next trigger
        } finally {
            checking = false;
        }
    };

    setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
    });
    window.addEventListener('focus', check);
    window.addEventListener('online', check);

    // The URL has already changed when this fires, so reloading lands the
    // user on the page they clicked, now served by the new build. REPLACE is
    // skipped: it is how the app redirects after saving.
    history.listen((location, action) => {
        if (action === 'REPLACE') return;
        if (newVersion && autoReloadOnNavigation && !isDirty()) window.location.reload();
    });
};

export const installChunkErrorReload = () => {
    window.addEventListener('unhandledrejection', event => {
        if (isChunkLoadError(event.reason) && !reloadOnce()) showNewVersionNotice();
    });
};
